import React, { useState, useEffect, useRef } from 'react';
import './SalesPage05.css';
import '../../components/HeroSection.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import HeroSection from '../../components/HeroSection';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';

export default function SalesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showLoginHint,
    loginReturnTo,
    getLeadSuccessPath,
  } = useSalesPromptRouting({ pageVariant: 'sales05' });

  const [newHeadlineIndex] = useState(2); // Use third headline variant

  // Add new ref for the sales page container
  const salesPageRef = useRef(null);

  useEffect(() => {
    // Enhanced page view tracking with UTM data
    const utmDataForPageView = getUtmParamsForApi();
    
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_page_05',
      source: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      variant: 'test', 
      experiment: 'sales_page_ab_test',
      $feature_flag_sales_page_variant_05: 'nate05', // PostHog experiment variant
      ...utmDataForPageView
    });
  }, [location]);



  // Define arrays for hero titles and highlighted texts
  const heroTitles = [
    "Create Profitable Trading Bots ", // Index 0 (Original/Control version)
    "Build Powerful, Automated Trading Bots in Minutes ", // Index 1 (New/Variant version)
    "Ditch Endless Coding. Build an AI Trading Bot in minutes."
    // Add more title options here if needed, e.g., heroTitles[2] = "Another Headline"
  ];

  const heroHighlightedTexts = [
    "Without Writing a Single Line of Code", // Index 0 (Original/Control version)
    "No Coding Required!", // Index 1 (New/Variant version)
    "No Coding Needed."
    // Add more highlighted text options here, corresponding to heroTitles
  ];

  const heroTitle = heroTitles[newHeadlineIndex] || heroTitles[0];
  const heroHighlightedText = heroHighlightedTexts[newHeadlineIndex] || heroHighlightedTexts[0];

  const handleLoginClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'sales_page_05_link',
      source: location.pathname
    });
    navigate(`/login?returnTo=${encodeURIComponent(loginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  // Handle lead capture success without putting auto-login tokens in URLs.
  const handleContactSuccess = ({ contactData, leadTransactionId, autoLoginToken, howFoundUs, howFoundUsOtherText, existingUser, redirectTo }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    // Build query params for prompt page
    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: 'sales05',
      howFoundUs,
      howFoundUsOtherText,
    });

    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  // Function to create random squares
  const createRandomSquares = () => {
    const container = salesPageRef.current;
    if (!container) return;

    // Remove existing squares
    const existingSquares = container.querySelectorAll('.floating-square');
    existingSquares.forEach(square => square.remove());

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Create new squares
    const numSquares = 15;
    for (let i = 0; i < numSquares; i++) {
      const square = document.createElement('div');
      square.className = 'floating-square';
      
      // Random size between 200px and 600px
      const size = Math.random() * 400 + 200;
      square.style.width = `${size}px`;
      square.style.height = `${size}px`;
      
      // Calculate position in pixels first
      const x = Math.random() * viewportWidth;
      // Distribute squares more evenly across the viewport height
      const y = Math.random() * (viewportHeight * 0.5); // Use 50% of viewport height
      
      // Convert to viewport-relative units (vw and vh)
      const xVw = (x / viewportWidth) * 100;
      const yVh = (y / viewportHeight) * 100;
      
      square.style.setProperty('--x', `${xVw}vw`);
      square.style.setProperty('--y', `${yVh-80}vh`); // -80 to center vertically
      
      // Random delay for animation
      square.style.animationDelay = `${Math.random() * 8}s`;
      
      // Random color with varying opacity
      const colors = [
        `rgba(58, 134, 255, ${Math.random() * 0.1 + 0.05})`,  // Blue
        `rgba(139, 92, 246, ${Math.random() * 0.1 + 0.05})`,  // Purple
        `rgba(34, 197, 94, ${Math.random() * 0.1 + 0.05})`    // Green
      ];
      square.style.background = colors[Math.floor(Math.random() * colors.length)];
      
      // Random rotation (reduced to 5 degrees max)
      square.style.transform = `rotate(${Math.random() * 5}deg)`;
      
      // Random animation duration (increased for slower movement)
      square.style.animationDuration = `${Math.random() * 4 + 8}s`;
      
      container.appendChild(square);
    }
  };

  // Initialize squares on mount and handle window resize
  useEffect(() => {
    createRandomSquares();
    
    const handleResize = () => {
      createRandomSquares();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const logoImages = [
    { src: "/images/logos/interactive-brokers.png", alt: "Interactive Brokers", width: "240", height: "32" }, 
    { src: "/images/logos/kucoin.png", alt: "Kucoin", width: "160", height: "32" },
    { src: "/images/logos/alpaca.png", alt: "Alpaca", width: "80", height: "32" }, 
    { src: "/images/logos/tradier.png", alt: "Tradier", width: "70", height: "32" }, 
    { src: "/images/logos/coinbase.png", alt: "Coinbase Pro", width: "200", height: "32" }, 
    { src: "/images/logos/binance.png", alt: "Binance", width: "170", height: "32" },
    { src: "/images/logos/schwab.png", alt: "Charles Schwab", width: "70", height: "32" },
    { src: "/images/logos/bitunix.png", alt: "BitUnix", width: "150", height: "32" },
    { src: "/images/logos/kraken.png", alt: "Kraken", width: "220", height: "32" },
    
  ];
  
  /* original logo images
  const logoImages = [
    { src: "/images/logos/interactive-brokers.png", alt: "Interactive Brokers" },
    { src: "/images/logos/alpaca.png", alt: "Alpaca" },
    { src: "/images/logos/tradier.png", alt: "Tradier" },
    { src: "/images/logos/coinbase.png", alt: "Coinbase" },
    { src: "/images/logos/kraken.png", alt: "Kraken" },
    { src: "/images/logos/tradeovate.png", alt: "Tradeovate" },
    { src: "/images/logos/ninjatrader.png", alt: "NinjaTrader" }
  ]; */

  // Define benefits data to pass to the component
  const benefitsData = [
    /* Nate removed. Rationalle: repeated info that takes time for the user to read and process*/
    /*{
      icon: <FaRobot className="feature-icon" />,
      title: "AI Bot Builder",
      description: "Turn your trading ideas into code in seconds without any programming knowledge"
    },*/
    {
      icon: 1, //<FaChartLine className="feature-icon" />,
      title: "Choose your bot type",
      description: "Grab a ready-made bot or chat with our AI to craft your own."
    },
    {
      icon: 2, //<FaGraduationCap className="feature-icon" />,
      title: "AI Codes & Back-tests",
      description: "BotSpot writes the code, runs a historical test, and shows win-rate & drawdown."
      },
    {
      icon: 3, //<FaUsers className="feature-icon" />,
      title: "Go Live or Paper",
      description: "Hook up your broker and let the bot trade automatically."
    }
  ];

  // Bypass rendering all UI since the goal is to just go to checkout.
  //return null;

  return (
    <div className="sales-page" ref={salesPageRef}>
      {/* SECTION: Customer Login Banner - Top banner for existing customers to log in 
      <div className="customer-login-banner">
        Already a customer? <button onClick={handleLogin} className="login-link">Log in here</button>
      </div> */}

      {/* SECTION: Scroll Progress Bar - Visual indicator of scroll progress */}
      {/* <ScrollProgressBar /> */}

      {/* New container for Hero Section and Inline Email Form */}
      <div className="sales-page-hero-and-form-container">
        <div className="sales-page-hero-wrapper">
          {/* SECTION: Hero Section - Main headline, video, features */}
          <HeroSection
            //preHeadline="INTRODUCING THE AI BOT BUILDER"
            title={heroTitle}
            highlightedTitleText={heroHighlightedText}
            subtitle="Describe your strategy in plain English. BotSpot writes, back-tests & deploys it for you."
            //features={heroFeatures} // features prop can be removed if not used, or kept for future use
            ctaText="Start Your Free Trial"

            customerCount="2,300+"
            logoImages={logoImages}
            benefitsData={benefitsData} // Pass benefitsData to HeroSection
            benefitsSectionTitle="Launch a Bot in 3 Steps" // Pass benefits section title
          />
        </div>

        {/* SECTION: Inline Checkout Form */}
        <div className="sales-page-inline-checkout-form">
          <div className="sales-page-inline-form-content">
            <h3>Start Your Free Trial</h3>
            <p className="sales-page-inline-form-subtitle">
              Get instant access to our AI Bot Builder platform. Join 2,300+ traders automating their strategies.
            </p>
            
            {/* Lead capture form - no payment, forwards to sales-ai-prompt */}
            {showLoginHint && (
              <div className="sales05-login-hint">
                Already a member?
                <button type="button" className="sales05-login-link" onClick={handleLoginClick}>
                  Log in
                </button>
              </div>
            )}
            <InlineCheckoutForm
              onContactSuccess={handleContactSuccess}
            />
          </div>
        </div>
      </div>

    </div>
  );
}
